import { PRICING_URL, VITE_FORGE_URL } from '@constants';
import forge from 'apis/forge';
import CreditTile from 'components/Account/CreditTile';
import CurrentPlanCard from 'components/Account/CurrentPlanCard';
import Header from 'components/Account/Header';
import Referral from 'components/Account/Referral';
import TierCard from 'components/Account/TierCard';
import Icon, { IconType } from 'components/Icon';
import ColorButton from 'components/Widget/Button/ColorButton';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import Chip from 'components/Widget/Chip';
import PricingToggle from 'components/Widget/PricingToggle';
import { Organization, OrganizationRole, Tier } from 'dragonmouse-shared/interfaces';
import { useCheckoutDataLayer } from 'hooks/useCheckoutDataLayer';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { updateGithubConnected, updateOrganizations } from 'redux/db/actions';
import {
	selectCreditWallet,
	selectFigmaConnected,
	selectGithubConnected,
	selectOrganizations,
	selectReferralInfo,
	selectUser,
} from 'redux/db/reducers';
import { showConfirmModal, showPromptModal } from 'redux/mode/actions';
import { selectIsMobile, selectSettings } from 'redux/mode/reducers';
import { displayPromptModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import type { UiColor } from 'tokens';
import { openFigmaAuthPopup, openGithubAuthPopup } from 'utils';
import * as Base from '../styles';
import McpIntegration from './McpIntegration';
import * as S from './styles';

const formatDate = (date: Date | string): string => {
	try {
		const dateObj = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(dateObj.getTime())) {
			console.error('Invalid date value', date);
			return '';
		}
		return new Intl.DateTimeFormat('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		}).format(dateObj);
	} catch (error) {
		console.error('Could not format date', date);
		return '';
	}
};

const formatCredits = (credits: number): string => {
	return credits.toLocaleString();
};

const Profile = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	useCheckoutDataLayer();
	const {
		authUser,
		user,
		subscription,
		isMobile,
		createOrganizationEnabled,
		organizations,
		creditWallet,
		figmaConnected,
		githubConnected,
		referralInfo,
	} = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
			user: selectUser(r.db),
			subscription: r.db.subscription,
			isMobile: selectIsMobile(r.mode),
			createOrganizationEnabled: selectSettings(r.mode).createOrganizationEnabled,
			organizations: selectOrganizations(r.db),
			creditWallet: selectCreditWallet(r.db),
			figmaConnected: selectFigmaConnected(r.db),
			githubConnected: selectGithubConnected(r.db),
			referralInfo: selectReferralInfo(r.db),
		}),
		shallowEqual
	);

	const [isYearly, setIsYearly] = useState(() => {
		return subscription ? subscription.interval === 'year' : true;
	});

	const displayName = user?.displayName || '';
	const currentTier = subscription?.tier ?? Tier.FREE;
	const hasActiveSubscription = currentTier !== Tier.FREE;

	useEffect(() => {
		if (hasActiveSubscription && subscription?.interval) {
			setIsYearly(subscription.interval === 'year');
		}
	}, [hasActiveSubscription, subscription?.interval]);

	const onChangeDisplayName = () => {
		displayPromptModal({
			title: 'Change Display Name',
			submitLabel: 'Save Changes',
			placeholder: 'Enter display name',
			initialValue: displayName,
			validate: (value) => {
				const trimmed = value.trim();
				if (!trimmed) {
					return 'Display name cannot be empty';
				}
				if (trimmed.length > 50) {
					return 'Display name must be 50 characters or less';
				}
				return null;
			},
			onSubmit: (newDisplayName) => {
				updateDisplayName(newDisplayName || displayName);
			},
		});
	};

	const updateDisplayName = async (newDisplayName: string) => {
		if (!user?.id) {
			toast.warning('Could not update display name due to internal error. Please contact support.');
			return;
		}

		await forge.updateUser(
			{
				update: {
					id: user.id,
					displayName: newDisplayName.trim(),
				},
			},
			true
		);
	};

	const renewalDate = creditWallet?.creditRenewDate
		? formatDate(creditWallet.creditRenewDate)
		: subscription?.currentPeriodEnd
		? formatDate(subscription.currentPeriodEnd)
		: '';

	const handleCreateOrganization = useCallback(() => {
		dispatch(
			showPromptModal({
				title: 'Create Team',
				description: 'Enter a name for your team',
				submitLabel: 'Create',
				placeholder: 'Team name',
				onSubmit: async (name) => {
					if (name && name.trim()) {
						const response = await forge.createOrganization({ name: name.trim() });
						dispatch(
							updateOrganizations([{ ...response.organization, role: 'owner' }, ...organizations])
						);
						toast.success('Team created');
					}
				},
			})
		);
	}, [dispatch, organizations]);

	const handleLeaveOrganization = useCallback(
		(org: Organization & { role: OrganizationRole }) => {
			dispatch(
				showConfirmModal({
					title: 'Leave Team',
					description: `Are you sure you want to leave "${org.name}"? You will lose access to all shared projects.`,
					cancelLabel: 'Cancel',
					submitLabel: 'Leave',
					onSubmit: async () => {
						try {
							await forge.leaveOrganization.withToast()(org.id);
							dispatch(updateOrganizations(organizations.filter((o) => o.id !== org.id)));
							toast.success('Left team');
						} catch (error) {
							console.error('Failed to leave organization:', error);
						}
					},
				})
			);
		},
		[dispatch, organizations]
	);

	const onConnectFigma = async () => {
		await openFigmaAuthPopup();
	};

	const onDisconnectFigma = async () => {
		await forge.disconnectFigma.withToast()();
	};

	const onConnectGithub = async () => {
		const ok = await openGithubAuthPopup();
		if (ok) dispatch(updateGithubConnected(true));
	};

	const onManageGithubRepos = async () => {
		await openGithubAuthPopup();
	};

	const onDisconnectGithub = async () => {
		await forge.disconnectGithub.withToast()();
	};

	const planBadgeColor: UiColor = currentTier === Tier.FREE ? 'gray' : 'blue';
	const planBadgeLabel =
		currentTier === Tier.FREE ? 'Free Plan' : currentTier === Tier.TIER_1 ? 'Pro Plan' : 'Max Plan';

	return (
		<Base.Base>
			<Base.Container>
				{isMobile && (
					<Base.SidebarToggle>
						<SidebarToggle />
					</Base.SidebarToggle>
				)}

				<Base.Content>
					<Header
						title={displayName}
						email={authUser?.email || ''}
						planBadgeColor={planBadgeColor}
						planBadgeLabel={planBadgeLabel}
						onEdit={onChangeDisplayName}
					/>

					{!hasActiveSubscription && (
						<Base.SubscriptionSection>
							<Base.SectionHeader>
								<Base.SectionTitle>Personal Subscription</Base.SectionTitle>
							</Base.SectionHeader>
							<Base.BillingBanner>
								<PricingToggle isYearly={isYearly} onChange={setIsYearly} />
								<Base.TierCards>
									<TierCard variant="upgrade" tier={Tier.TIER_1} isYearly={isYearly} />
									<TierCard variant="upgrade" tier={Tier.TIER_2} isYearly={isYearly} />
								</Base.TierCards>
							</Base.BillingBanner>
							<Base.PricingLinkWrapper>
								<Base.PricingLink href={PRICING_URL} target="_blank" rel="noopener noreferrer">
									View detailed pricing →
								</Base.PricingLink>
							</Base.PricingLinkWrapper>
						</Base.SubscriptionSection>
					)}

					{creditWallet && (
						<Base.CreditDashboard>
							<CreditTile
								icon={IconType.REFRESH}
								iconColor={theme.blue[400]}
								label="Monthly Credits"
								amount={formatCredits(creditWallet.credits)}
								subtext={renewalDate ? `Renews on ${renewalDate}` : 'Part of your subscription'}
							/>
							{creditWallet.promotionalCredits > 0 && (
								<CreditTile
									icon={IconType.GIFT}
									iconColor={theme.purple[500]}
									label="Promotional Credits"
									amount={formatCredits(creditWallet.promotionalCredits)}
									subtext={
										creditWallet.lastPromotionalDate
											? `Last awarded on ${formatDate(creditWallet.lastPromotionalDate)}`
											: ''
									}
								/>
							)}
							<CreditTile
								icon={IconType.CREDIT_CARD}
								iconColor={theme.green[400]}
								label="Top-up Credits"
								amount={formatCredits(creditWallet.purchaseCredits)}
								subtext={
									creditWallet.lastTopUpDate
										? `Last purchased on ${formatDate(creditWallet.lastTopUpDate)}`
										: 'No purchases yet'
								}
								button={{
									label: 'Buy More Credits',
									onClick: () => window.open(`${VITE_FORGE_URL}/stripe/credit-purchase`),
									icon: IconType.ADD,
								}}
							/>
						</Base.CreditDashboard>
					)}

					{currentTier !== Tier.FREE && (
						<Base.SubscriptionSection>
							<Base.SectionHeader>
								<Base.SectionTitle>Personal Subscription</Base.SectionTitle>
							</Base.SectionHeader>
							<CurrentPlanCard tier={currentTier} renewalDate={renewalDate} />
						</Base.SubscriptionSection>
					)}

					{referralInfo && <Referral referralInfo={referralInfo} />}

					<Base.Section>
						<Base.SectionHeader>
							<Base.SectionTitle>Integrations</Base.SectionTitle>
						</Base.SectionHeader>
						<S.IntegrationRow>
							<S.IntegrationInfo>
								<Icon type={IconType.FIGMA} width={20} height={20} color={theme.gray[800]} />
								<S.IntegrationName>Figma</S.IntegrationName>
								{figmaConnected && <S.IntegrationStatus>Connected</S.IntegrationStatus>}
							</S.IntegrationInfo>
							{figmaConnected ? (
								<S.IntegrationButton onClick={onDisconnectFigma}>Disconnect</S.IntegrationButton>
							) : (
								<S.IntegrationButton onClick={onConnectFigma}>Connect</S.IntegrationButton>
							)}
						</S.IntegrationRow>
						<S.IntegrationRow>
							<S.IntegrationInfo>
								<Icon type={IconType.GITHUB} width={20} height={20} color={theme.gray[800]} />
								<S.IntegrationName>GitHub</S.IntegrationName>
								{githubConnected && <S.IntegrationStatus>Connected</S.IntegrationStatus>}
							</S.IntegrationInfo>
							{githubConnected ? (
								<S.IntegrationButtonGroup>
									<S.IntegrationButton onClick={onManageGithubRepos}>
										Manage repos
									</S.IntegrationButton>
									<S.IntegrationButton onClick={onDisconnectGithub}>Disconnect</S.IntegrationButton>
								</S.IntegrationButtonGroup>
							) : (
								<S.IntegrationButton onClick={onConnectGithub}>Connect</S.IntegrationButton>
							)}
						</S.IntegrationRow>
						<McpIntegration />
					</Base.Section>

					{(createOrganizationEnabled || organizations.length > 0) && (
						<Base.Section>
							<S.OrgHeader>
								<Base.SectionTitle>Teams</Base.SectionTitle>
							</S.OrgHeader>
							<S.OrgGrid>
								{organizations.map((org) => (
									<S.OrgCard key={org.id}>
										<S.OrgCardHeader>
											<S.OrgInfo>
												<S.OrgName>{org.name}</S.OrgName>
												<Chip
													label={org.role === 'owner' ? 'Owner' : 'Member'}
													color={org.role === 'owner' ? 'purple' : 'blue'}
												/>
											</S.OrgInfo>
										</S.OrgCardHeader>
										<S.OrgStats>
											<S.OrgStat>
												<S.OrgStatLabel>Team Members</S.OrgStatLabel>
												<S.OrgStatValue>---</S.OrgStatValue>
											</S.OrgStat>
											<S.OrgStat>
												<S.OrgStatLabel>Team Credits</S.OrgStatLabel>
												<S.OrgStatValue>---</S.OrgStatValue>
											</S.OrgStat>
										</S.OrgStats>
										<S.OrgActions>
											{org.role === 'owner' ? (
												<ColorButton
													label="Manage Team"
													icon={IconType.SETTINGS}
													color="purple"
													fullWidth
													onClick={() => navigate(routes.organization(org.id))}
												/>
											) : (
												<>
													<S.ViewButton onClick={() => navigate(routes.organization(org.id))}>
														View Details
													</S.ViewButton>
													<S.LeaveOrgButton onClick={() => handleLeaveOrganization(org)}>
														Leave
													</S.LeaveOrgButton>
												</>
											)}
										</S.OrgActions>
									</S.OrgCard>
								))}
								{createOrganizationEnabled && (
									<S.CreateOrgTileWrapper>
										<S.CreateOrgHeader>New Team</S.CreateOrgHeader>
										<S.CreateOrgFeatureList>
											<S.CreateOrgFeature>Shared team credits</S.CreateOrgFeature>
											<S.CreateOrgFeature>Collaborate on projects</S.CreateOrgFeature>
											<S.CreateOrgFeature>Manage members</S.CreateOrgFeature>
										</S.CreateOrgFeatureList>
										<ColorButton
											label="Create Team"
											icon={IconType.ADD}
											color="purple"
											fullWidth
											onClick={handleCreateOrganization}
										/>
									</S.CreateOrgTileWrapper>
								)}
							</S.OrgGrid>
						</Base.Section>
					)}
				</Base.Content>
			</Base.Container>
		</Base.Base>
	);
};

export default Profile;
